import { MenuItem } from "@mui/material";
import { Form } from "formik";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { getArtPrestadores } from "../../../../actions/arts";
import { convertDateTimeToNet } from "../../../../utils/datetimeUtils";
import {
  FormikDateRangeInput,
  FormikDateRangeSearch,
} from "../../../commons/formik/formikDateRangeSearch";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikForm from "../../../commons/formik/formikForm";
import FormikSelect from "../../../commons/formik/formikSelect";
import FormikTextField from "../../../commons/formik/formikTextField";
import { StyledForm } from "../../../styled/form";
import { StyledPaper } from "../../../styled/paper";

const FORM_INITIAL_VALUES = {
  idPrestador: "",
  siniestro: "",
  dni: "",
  usuario: "",
  email: "",
  idMovimiento: null,
  datePeriod: "7d",
  dateFrom: null,
  dateTo: null,
};

const validationSchema = yup.object().shape(
  {
    idPrestador: yup.string().notRequired(),
    siniestro: yup.string().notRequired(),
    dni: yup.string().notRequired(),
    usuario: yup.string().notRequired(),
    email: yup.string().notRequired(),
    idMovimiento: yup.number().nullable().notRequired(),
    datePeriod: yup.string().optional(),
    dateFrom: yup
      .date()
      .nullable()
      .notRequired()
      .when("dateTo", {
        is: (dateTo) => dateTo !== null && dateTo !== undefined,
        then: (schema) => schema.required("Ingrese una fecha desde la que buscar"),
      })
      .test(
        "date-from-before-to",
        "La fecha desde debe ser anterior o igual a la fecha hasta",
        function (value) {
          const { dateTo } = this.parent;
          if (!value || !dateTo) return true;
          return new Date(value) <= new Date(dateTo);
        },
      ),
    dateTo: yup
      .date()
      .nullable()
      .notRequired()
      .when("dateFrom", {
        is: (dateFrom) => dateFrom !== null && dateFrom !== undefined,
        then: (schema) => schema.required("Ingrese una fecha hasta la que buscar"),
      })
      .test(
        "date-to-after-from",
        "La fecha hasta debe ser posterior o igual a la fecha desde",
        function (value) {
          const { dateFrom } = this.parent;
          if (!value || !dateFrom) return true;
          return new Date(value) >= new Date(dateFrom);
        },
      ),
  },
  [["dateFrom", "dateTo"]],
).test(
  "at-least-one-filter",
  "Ingrese al menos un criterio de búsqueda además del período.",
  function validateAtLeastOneFilter(values) {
    const hasPrestador = values.idPrestador !== "" && values.idPrestador != null;
    const hasSiniestro = Boolean(values.siniestro?.trim());
    const hasDni = Boolean(values.dni?.trim());
    const hasUsuario = Boolean(values.usuario?.trim());
    const hasEmail = Boolean(values.email?.trim());
    const hasAccion = values.idMovimiento != null && values.idMovimiento !== "";
    const hasAnyFilter = hasPrestador || hasSiniestro || hasDni || hasUsuario || hasEmail || hasAccion;
    return hasAnyFilter || this.createError({
      path: "idPrestador",
      message: "Ingrese al menos un criterio de búsqueda además del período.",
    });
  },
);

const AuditoriaPrestadoresSearch = ({ isSearching, actionTypes, handleSearch, handleReset }) => {
  const dispatch = useDispatch();
  const prestadores = useSelector((state) => state.arts.artPrestadores?.data || []);

  useEffect(() => {
    dispatch(getArtPrestadores());
  }, [dispatch]);

  return (
    <div>
      <StyledPaper.Container>
        <FormikForm
          initialValues={FORM_INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleSearch({
              idPrestador: values.idPrestador || undefined,
              siniestro: values.siniestro || undefined,
              numeroDocumento: values.dni || undefined,
              usuario: values.usuario || undefined,
              email: values.email || undefined,
              idMovimiento: values.idMovimiento || undefined,
              fechaDesde: values.dateFrom ? convertDateTimeToNet(values.dateFrom) : undefined,
              fechaHasta: values.dateTo ? convertDateTimeToNet(values.dateTo) : undefined,
            });
          }}>
          {({ handleSubmit, setFieldValue }) => (
            <>
              <Form>
                <StyledForm.Input>
                  <FormikSelect name="idPrestador" label="Prestador" variant="standard">
                    <MenuItem value="">Todos</MenuItem>
                    {prestadores.map((prestador) => (
                      <MenuItem key={prestador.id} value={prestador.id}>
                        {prestador.nombre}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikTextField name="siniestro" label="Siniestro" variant="standard" />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikTextField name="dni" label="Documento Trabajador" variant="standard" />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikTextField name="usuario" label="Usuario" variant="standard" />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikTextField name="email" label="Email" variant="standard" />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikAutocomplete
                    name="idMovimiento"
                    label="Acción"
                    variant="standard"
                    showAllOptions
                    options={(actionTypes || []).map((movimiento) => ({
                      id: movimiento.idMovimiento,
                      nombre: movimiento.descripcion,
                    }))}
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeSearch
                    name="datePeriod"
                    label="Período"
                    variant="standard"
                    startDateFieldName="dateFrom"
                    endDateFieldName="dateTo"
                  />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeInput name="dateFrom" label="Fecha Desde" />
                </StyledForm.Input>
                <StyledForm.Input>
                  <FormikDateRangeInput name="dateTo" label="Fecha Hasta" />
                </StyledForm.Input>
              </Form>
              <StyledForm.ButtonContainer>
                <StyledForm.Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={isSearching}>
                  Buscar
                </StyledForm.Button>
              </StyledForm.ButtonContainer>
              <StyledForm.ButtonContainer>
                <StyledForm.Button
                  variant="contained"
                  type="reset"
                  color="primary"
                  onClick={() => {
                    setFieldValue("idPrestador", FORM_INITIAL_VALUES.idPrestador);
                    setFieldValue("siniestro", FORM_INITIAL_VALUES.siniestro);
                    setFieldValue("dni", FORM_INITIAL_VALUES.dni);
                    setFieldValue("usuario", FORM_INITIAL_VALUES.usuario);
                    setFieldValue("email", FORM_INITIAL_VALUES.email);
                    setFieldValue("idMovimiento", FORM_INITIAL_VALUES.idMovimiento);
                    setFieldValue("datePeriod", FORM_INITIAL_VALUES.datePeriod);
                    setFieldValue("dateFrom", FORM_INITIAL_VALUES.dateFrom);
                    setFieldValue("dateTo", FORM_INITIAL_VALUES.dateTo);
                    handleReset();
                  }}>
                  Limpiar
                </StyledForm.Button>
              </StyledForm.ButtonContainer>
            </>
          )}
        </FormikForm>
      </StyledPaper.Container>
    </div>
  );
};

export default AuditoriaPrestadoresSearch;
